import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { ChevronRight, ChevronLeft, Utensils, Fish, Repeat, ChefHat } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => {
  if (!src) return "";
  if (src.startsWith("http")) return src;
  if (src.startsWith("/api/")) return `${BACKEND_URL}${src}`;
  return src;
};

const HERO_BG = [
  "https://static.prod-images.emergentagent.com/jobs/677e9ea1-56f6-4d31-b3e8-677001a12cb0/images/5864be5acc5cbb49c73b4e3e18efa13c1c3f5e5e813991ba4bfafa48afc30e77.png",
  "https://static.prod-images.emergentagent.com/jobs/677e9ea1-56f6-4d31-b3e8-677001a12cb0/images/bca99be5764f5191182ee9a1421c0085231f303e8dd30397dade955ffb995154.png",
];

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/products", { params: { in_stock_only: true } }).then((r) => setProducts(r.data));
  }, []);

  useEffect(() => {
    if (products.length === 0) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % products.length), 5000);
    return () => clearInterval(t);
  }, [products.length]);

  const current = products[idx];
  const bgImg = HERO_BG[idx % HERO_BG.length];

  return (
    <div data-testid="home-page">
      <SEO
        title="Premium Ocean-Fresh Seafood, Smoked & Dried Fish"
        description="Fresh, smoked, dried and frozen fish delivered across the UK. Book chef-led cooking classes, weekly subscriptions, and private event chefs. Pay securely in GBP."
        path="/"
      />
      {/* HERO SLIDER */}
      <section className="relative overflow-hidden bg-[color:var(--color-primary)] text-white">
        {current && (
          <div className="absolute inset-0">
            <div
              className="absolute inset-0 ken-burns"
              style={{
                backgroundImage: `linear-gradient(rgba(15,42,63,0.78), rgba(15,42,63,0.5)), url(${bgImg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </div>
        )}
        <div className="relative container-x px-6 pt-24 pb-32 md:pt-32 md:pb-40 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-in">
            <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--color-accent)] mb-5">
              Ocean to Table — Sustainably Sourced
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] font-medium mb-6">
              {current?.name || "Premium Seafood, Delivered."}
            </h1>
            <p className="text-white/80 max-w-xl text-lg leading-relaxed mb-8">
              {current?.description || "Hand-selected, ethically sourced, and packed with care. Discover seafood the way it was meant to be."}
            </p>
            <div className="flex flex-wrap gap-4">
              {current && (
                <Link to={`/products/${current.id}`} className="btn-accent" data-testid="hero-view-details-btn">
                  View Details / Order <ChevronRight className="w-4 h-4" />
                </Link>
              )}
              <Link to="/products" className="btn-ghost border-white text-white hover:bg-white hover:text-[color:var(--color-primary)]" data-testid="hero-shop-all-btn">
                Shop All Fish
              </Link>
            </div>
            {products.length > 1 && (
              <div className="flex items-center gap-4 mt-12">
                <button onClick={() => setIdx((i) => (i - 1 + products.length) % products.length)} className="border border-white/30 hover:border-white p-2 rounded-full" data-testid="hero-prev">
                  <ChevronLeft className="w-5 h-5"/>
                </button>
                <div className="flex gap-2">
                  {products.map((_, i) => (
                    <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i+1}`} className={`h-1 transition-all ${i === idx ? "w-10 bg-[color:var(--color-accent)]" : "w-5 bg-white/30"}`} data-testid={`hero-dot-${i}`} />
                  ))}
                </div>
                <button onClick={() => setIdx((i) => (i + 1) % products.length)} className="border border-white/30 hover:border-white p-2 rounded-full" data-testid="hero-next">
                  <ChevronRight className="w-5 h-5"/>
                </button>
              </div>
            )}
          </div>
          {current && (
            <div className="lg:col-span-5 lg:pl-8">
              <div className="relative aspect-[4/5] rounded-lg overflow-hidden shadow-2xl">
                <img src={resolveImg(current.images?.[0])} alt={current.name} className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4 bg-[color:var(--color-accent)] text-white text-xs px-3 py-1 uppercase tracking-widest rounded">
                  In Stock
                </div>
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur rounded p-4">
                  <div className="font-display text-2xl text-[color:var(--color-primary)]">£{current.price?.toFixed(2)}</div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">{current.weight} · {current.category}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 4 CATEGORY CTAs */}
      <section className="section bg-[color:var(--color-bg)]">
        <div className="container-x">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--color-accent)] mb-3">Explore Our World</div>
            <h2 className="font-display text-4xl md:text-5xl text-[color:var(--color-primary)]">More Than Just Fresh Fish</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { to: "/products", icon: Fish, label: "Fish Products", desc: "Fresh, smoked, dried & frozen", testid: "cta-products" },
              { to: "/classes", icon: Utensils, label: "Cooking Classes", desc: "Learn from our chefs", testid: "cta-classes" },
              { to: "/subscriptions", icon: Repeat, label: "Subscription Plans", desc: "Weekly fish, delivered", testid: "cta-subscriptions" },
              { to: "/event-chef", icon: ChefHat, label: "Event Chef", desc: "We cook at your event", testid: "cta-event-chef" },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={c.testid}
                className="group bg-white border border-[color:var(--color-border)] rounded-lg p-8 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                <c.icon className="w-9 h-9 text-[color:var(--color-accent)] mb-6 group-hover:scale-110 transition" />
                <h3 className="font-display text-2xl text-[color:var(--color-primary)] mb-2">{c.label}</h3>
                <p className="text-sm text-[color:var(--color-text-secondary)] mb-6">{c.desc}</p>
                <div className="flex items-center gap-1 text-xs uppercase tracking-widest text-[color:var(--color-accent)] font-medium">
                  Discover <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* IN-STOCK GRID */}
      <section className="section bg-white">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--color-accent)] mb-3">Today's Catch</div>
              <h2 className="font-display text-4xl md:text-5xl text-[color:var(--color-primary)]">In-Stock & Ready</h2>
            </div>
            <Link to="/products" className="btn-ghost" data-testid="home-shop-all">View all products</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.slice(0, 6).map((p) => (
              <Link key={p.id} to={`/products/${p.id}`} className="product-card block bg-white border border-[color:var(--color-border)] rounded-lg overflow-hidden" data-testid={`home-product-${p.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  <img src={resolveImg(p.images?.[0])} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display text-2xl text-[color:var(--color-primary)]">{p.name}</h3>
                    <span className="text-xs uppercase tracking-widest text-emerald-600">In Stock</span>
                  </div>
                  <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">{p.weight} · {p.category}</div>
                  <div className="flex items-center justify-between">
                    <div className="font-display text-2xl text-[color:var(--color-accent)]">£{p.price?.toFixed(2)}</div>
                    <span className="text-xs uppercase tracking-widest text-[color:var(--color-primary)]">Order →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
